import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../utils/api';
import { ProductCard, StoreHeader, StoreSkeletonGrid } from '../components/store/StoreUi';

export default function Store() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [goldenTickets, setGoldenTickets] = useState(null);
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('all');
  const [sort, setSort] = useState('featured');
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadProducts = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await api.get('/store/products', {
        params: {
          page,
          limit: 12,
          category: category === 'all' ? undefined : category,
          search: query || undefined,
          sort,
        },
      });
      setProducts(data?.items || []);
      setCategories(data?.categories || []);
      setGoldenTickets(data?.goldenTickets ?? null);
      setPages(data?.pages || 1);
    } catch (err) {
      setError(err?.response?.data?.message || 'The store could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, [page, category, query, sort]);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  useEffect(() => {
    setPage(1);
  }, [category, query, sort]);

  return (
    <main className="min-h-screen bg-slate-50 dark:bg-[#090d16] py-8 sm:py-10">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <StoreHeader
          goldenTickets={goldenTickets}
          subtitle="Turn your winning streak into real rewards. Shop jerseys, sneakers, collectibles, and more using golden tickets."
        />

        <div className="mb-6 grid gap-3 rounded-2xl border border-slate-200 dark:border-white/10 bg-white dark:bg-slate-900 p-3 sm:grid-cols-[1fr_auto] sm:p-4">
          <form
            onSubmit={(event) => {
              event.preventDefault();
              setQuery(search.trim());
            }}
            className="flex gap-2"
          >
            <label className="relative flex-1">
              <span className="sr-only">Search the store</span>
              <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-slate-400">⌕</span>
              <input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search jerseys, sneakers, collectibles…"
                className="w-full rounded-xl border border-slate-200 dark:border-white/10 bg-slate-50 dark:bg-black/20 py-2.5 pl-10 pr-3 text-sm text-slate-900 dark:text-white outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20"
              />
            </label>
            <button className="rounded-xl bg-slate-950 dark:bg-white px-4 text-sm font-bold text-white dark:text-slate-950">
              Search
            </button>
          </form>
          <div className="flex gap-2">
            <select
              value={sort}
              onChange={(event) => setSort(event.target.value)}
              className="min-w-0 flex-1 rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-slate-900 px-3 py-2.5 text-sm text-slate-700 dark:text-slate-200 sm:w-44"
            >
              <option value="featured">Featured</option>
              <option value="newest">Newest</option>
              <option value="popular">Most popular</option>
              <option value="price-low">Price: low to high</option>
              <option value="price-high">Price: high to low</option>
            </select>
            <Link
              to="/store/orders"
              className="inline-flex items-center whitespace-nowrap rounded-xl border border-slate-200 dark:border-white/10 px-3 py-2.5 text-sm font-bold text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-white/5"
            >
              My orders
            </Link>
          </div>
        </div>

        {categories.length > 0 && (
          <div className="mb-7 flex gap-2 overflow-x-auto pb-1">
            {['all', ...categories].map((item) => (
              <button
                key={item}
                type="button"
                onClick={() => setCategory(item)}
                className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-bold transition-colors ${
                  category === item
                    ? 'bg-amber-400 text-slate-950 shadow-sm'
                    : 'border border-slate-200 dark:border-white/10 bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-300'
                }`}
              >
                {item === 'all' ? 'All rewards' : item}
              </button>
            ))}
          </div>
        )}

        {loading ? (
          <StoreSkeletonGrid />
        ) : error ? (
          <div className="rounded-2xl border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950/20 p-8 text-center">
            <p className="font-semibold text-red-700 dark:text-red-300">{error}</p>
            <button onClick={loadProducts} className="mt-3 text-sm font-bold text-red-700 dark:text-red-300 underline">
              Try again
            </button>
          </div>
        ) : products.length === 0 ? (
          <div className="rounded-3xl border border-dashed border-slate-300 dark:border-white/15 bg-white dark:bg-slate-900/60 px-6 py-16 text-center">
            <p className="text-5xl" aria-hidden>🎁</p>
            <h2 className="mt-4 text-xl font-bold text-slate-900 dark:text-white">No rewards found</h2>
            <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">Try another search or category.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {products.map((product) => <ProductCard key={product._id} product={product} />)}
          </div>
        )}

        {pages > 1 && (
          <div className="mt-10 flex items-center justify-center gap-3">
            <button
              disabled={page <= 1}
              onClick={() => setPage((value) => value - 1)}
              className="rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-slate-900 px-4 py-2 text-sm font-bold disabled:opacity-40"
            >
              Previous
            </button>
            <span className="text-sm text-slate-500 dark:text-slate-400">Page {page} of {pages}</span>
            <button
              disabled={page >= pages}
              onClick={() => setPage((value) => value + 1)}
              className="rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-slate-900 px-4 py-2 text-sm font-bold disabled:opacity-40"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </main>
  );
}
